import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listJournal, saveJournal, deleteJournal, exportJournal } from "@/lib/journey.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { GATES } from "@/lib/gates";
import { Pin, Trash2, Download, Plus, Search } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/journey/journal")({
  component: JournalPage,
});

type Entry = Awaited<ReturnType<typeof listJournal>>[number];

const PROMPTS = [
  "What is present with me right now?",
  "Where did I return to my breath today?",
  "What quietly kept me discipline today?",
  "What am I ready to release?",
  "How did today's Gate move through me?",
];

function JournalPage() {
  const load = useServerFn(listJournal);
  const save = useServerFn(saveJournal);
  const del = useServerFn(deleteJournal);
  const exp = useServerFn(exportJournal);

  const [entries, setEntries] = useState<Entry[]>([]);
  const [selected, setSelected] = useState<Entry | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [gate, setGate] = useState<number | null>(null);
  const [pinned, setPinned] = useState(false);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string>("");
  const saveTimer = useRef<number | null>(null);

  const refresh = () => load({ data: { search: search || undefined } }).then(setEntries).catch(() => {});
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [search]);

  useEffect(() => {
    if (!selected) return;
    setTitle(selected.title ?? "");
    setBody(selected.body);
    setGate(selected.gate_n ?? null);
    setPinned(selected.is_pinned);
  }, [selected]);

  // Auto-save
  useEffect(() => {
    if (!selected && !body) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      if (!body.trim()) return;
      setSaving(true);
      try {
        const row = await save({
          data: { id: selected?.id, title, body, gate_n: gate, is_pinned: pinned },
        });
        setSelected(row);
        setLastSaved(new Date().toLocaleTimeString());
        await refresh();
      } catch (e) { toast.error((e as Error).message); }
      finally { setSaving(false); }
    }, 900);
    return () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, body, gate, pinned]);

  const wordCount = useMemo(() => body.trim().split(/\s+/).filter(Boolean).length, [body]);

  const newEntry = () => {
    setSelected(null); setTitle(""); setBody(""); setGate(null); setPinned(false); setLastSaved("");
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!confirm("Delete this reflection permanently?")) return;
    await del({ data: { id: selected.id } });
    newEntry(); refresh();
  };

  const handleExport = async () => {
    const data = await exp();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `kafrequency-journal-${new Date().toISOString().slice(0,10)}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
      <aside className="space-y-3">
        <div className="flex gap-2">
          <Button size="sm" onClick={newEntry}><Plus size={14} className="mr-1" />New</Button>
          <Button size="sm" variant="outline" onClick={handleExport}><Download size={14} className="mr-1" />Export</Button>
        </div>
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-foreground/50" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reflections" className="pl-7" />
        </div>
        <ul className="max-h-[70vh] space-y-2 overflow-y-auto">
          {entries.length === 0 && <li className="text-sm text-foreground/60">No entries yet. Write your first reflection.</li>}
          {entries.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => setSelected(e)}
                className={`w-full rounded-md border p-3 text-left transition-colors ${
                  selected?.id === e.id ? "border-gold bg-gold/5" : "hairline hover:border-gold/50"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="line-clamp-1 text-sm font-medium">{e.title || "Untitled"}</span>
                  {e.is_pinned && <Pin size={12} className="text-gold" />}
                </div>
                <div className="mt-1 line-clamp-2 text-xs text-foreground/60">{e.body}</div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-foreground/40">
                  {new Date(e.updated_at).toLocaleDateString()}
                  {e.gate_n && ` · Gate ${e.gate_n}`}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <Card className="p-4 sm:p-6">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="max-w-md" />
          <select
            value={gate ?? ""}
            onChange={(e) => setGate(e.target.value ? Number(e.target.value) : null)}
            className="rounded-md border hairline bg-obsidian px-2 py-2 text-sm"
            aria-label="Link to a Gate"
          >
            <option value="">No Gate</option>
            {GATES.map((g) => <option key={g.n} value={g.n}>Gate {g.n} · {g.title}</option>)}
          </select>
          <Button
            variant="ghost" size="sm"
            onClick={() => setPinned((p) => !p)}
            aria-pressed={pinned}
            className={pinned ? "text-gold" : ""}
          >
            <Pin size={14} className="mr-1" />{pinned ? "Pinned" : "Pin"}
          </Button>
          <div className="ml-auto flex items-center gap-2">
            {selected && (
              <Button variant="ghost" size="sm" onClick={handleDelete} aria-label="Delete entry">
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        </div>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onFocus={(e) => {
            const el = e.currentTarget;
            setTimeout(() => el.scrollIntoView({ block: "center", behavior: "smooth" }), 300);
          }}
          placeholder={PROMPTS[Math.floor(Math.random() * PROMPTS.length)]}
          className="min-h-[40dvh] resize-y font-serif text-base leading-relaxed scroll-mt-24"
          aria-label="Journal body"
        />
        <div className="mt-2 flex justify-between text-xs text-foreground/50">
          <span>{wordCount} words</span>
          <span>{saving ? "Saving…" : lastSaved ? `Saved ${lastSaved}` : ""}</span>
        </div>
      </Card>
    </div>
  );
}
